import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Users,
  Video,
  DollarSign,
  TrendingUp,
  Crown,
  Loader2,
  MoreVertical,
  Play,
  Plus,
  Trash2,
  Edit,
  Bell,
  Palette,
  Type,
  Tag,
  BarChart3,
  Settings,
  Megaphone
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [captionStyles, setCaptionStyles] = useState([]);
  const [videoTypes, setVideoTypes] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddStyle, setShowAddStyle] = useState(false);
  const [showAddType, setShowAddType] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Form states
  const [newStyle, setNewStyle] = useState({ id: '', name: '', preview: '' });
  const [newType, setNewType] = useState({ id: '', name: '', description: '' });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', type: 'info', target: 'all' });
  const [editingPrice, setEditingPrice] = useState({ price: 0, features: [] });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      const [statsRes, usersRes, projectsRes, paymentsRes, stylesRes, typesRes, transitionsRes, announcementsRes, pricingRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getProjects(),
        adminAPI.getPayments(),
        adminAPI.getCaptionStyles(),
        adminAPI.getVideoTypes(),
        adminAPI.getTransitions(),
        adminAPI.getAnnouncements(),
        adminAPI.getPricing()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setPayments(paymentsRes.data);
      setCaptionStyles(stylesRes.data);
      setVideoTypes(typesRes.data);
      setTransitions(transitionsRes.data);
      setAnnouncements(announcementsRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await adminAPI.updateUser(userId, data);
      toast.success('User updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await adminAPI.deleteProject(projectId);
      toast.success('Project deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleAddCaptionStyle = async () => {
    try {
      await adminAPI.createCaptionStyle(newStyle);
      toast.success('Caption style added');
      setShowAddStyle(false);
      setNewStyle({ id: '', name: '', preview: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add style');
    }
  };

  const handleDeleteCaptionStyle = async (styleId) => {
    try {
      await adminAPI.deleteCaptionStyle(styleId);
      toast.success('Caption style deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete style');
    }
  };

  const handleAddVideoType = async () => {
    try {
      await adminAPI.createVideoType(newType);
      toast.success('Video type added');
      setShowAddType(false);
      setNewType({ id: '', name: '', description: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add type');
    }
  };

  const handleDeleteVideoType = async (typeId) => {
    try {
      await adminAPI.deleteVideoType(typeId);
      toast.success('Video type deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete type');
    }
  };

  const handleAddAnnouncement = async () => {
    try {
      await adminAPI.createAnnouncement(newAnnouncement);
      toast.success('Announcement created');
      setShowAddAnnouncement(false);
      setNewAnnouncement({ title: '', message: '', type: 'info', target: 'all' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      await adminAPI.deleteAnnouncement(announcementId);
      toast.success('Announcement deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncement = async (announcementId) => {
    try {
      await adminAPI.toggleAnnouncement(announcementId);
      toast.success('Announcement toggled');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to toggle announcement');
    }
  };

  const handleUpdatePricing = async () => {
    try {
      await adminAPI.updatePricing(selectedPlan.plan_id, {
        plan_id: selectedPlan.plan_id,
        price: parseFloat(editingPrice.price),
        features: editingPrice.features
      });
      toast.success('Pricing updated');
      setShowEditPricing(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update pricing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                <Play className="w-4 h-4 text-black fill-black" />
              </div>
              <h1 className="font-heading font-semibold text-xl text-white">Admin Dashboard</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {[
              { label: 'Total Users', value: stats?.total_users || 0, icon: <Users className="w-5 h-5" />, color: 'text-[#CCFF00]' },
              { label: 'Pro Users', value: stats?.pro_users || 0, icon: <Crown className="w-5 h-5" />, color: 'text-[#6E44FF]' },
              { label: 'Total Projects', value: stats?.total_projects || 0, icon: <Video className="w-5 h-5" />, color: 'text-blue-500' },
              { label: 'Revenue (CHF)', value: stats?.total_revenue?.toFixed(2) || '0.00', icon: <DollarSign className="w-5 h-5" />, color: 'text-green-500' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${stat.color}`}>
                      {stat.icon}
                    </div>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-white/60 text-sm mb-1">{stat.label}</p>
                  <p className="font-heading font-bold text-2xl text-white">{stat.value}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="bg-[#0A0A0A] border border-white/10 p-1 rounded-xl flex-wrap">
              <TabsTrigger value="users" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <Users className="w-4 h-4 mr-2" />Users
              </TabsTrigger>
              <TabsTrigger value="projects" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <Video className="w-4 h-4 mr-2" />Projects
              </TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <DollarSign className="w-4 h-4 mr-2" />Payments
              </TabsTrigger>
              <TabsTrigger value="content" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <Palette className="w-4 h-4 mr-2" />Content
              </TabsTrigger>
              <TabsTrigger value="pricing" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <Tag className="w-4 h-4 mr-2" />Pricing
              </TabsTrigger>
              <TabsTrigger value="announcements" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <Megaphone className="w-4 h-4 mr-2" />Announcements
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black rounded-lg">
                <BarChart3 className="w-4 h-4 mr-2" />Analytics
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">User</TableHead>
                      <TableHead className="text-white/60">Email</TableHead>
                      <TableHead className="text-white/60">Role</TableHead>
                      <TableHead className="text-white/60">Status</TableHead>
                      <TableHead className="text-white/60">Joined</TableHead>
                      <TableHead className="text-white/60">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#CCFF00]/20 flex items-center justify-center text-[#CCFF00] font-semibold text-sm">
                                {u.name?.charAt(0) || 'U'}
                              </div>
                            )}
                            <span className="text-white">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-white/60">{u.email}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' ? 'bg-[#6E44FF]/20 text-[#6E44FF]' : 'bg-white/10 text-white/60'
                          }`}>
                            {u.role}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.subscription_status === 'pro' ? 'bg-[#CCFF00]/20 text-[#CCFF00]' : 'bg-white/10 text-white/60'
                          }`}>
                            {u.subscription_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-white/60">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-white/40 hover:text-white">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
                              <DropdownMenuItem onClick={() => handleUpdateUser(u.user_id, { role: u.role === 'admin' ? 'user' : 'admin' })}>
                                {u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateUser(u.user_id, { subscription_status: u.subscription_status === 'pro' ? 'free' : 'pro' })}>
                                {u.subscription_status === 'pro' ? 'Remove Pro' : 'Grant Pro'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateUser(u.user_id, { is_active: !u.is_active })} className={u.is_active ? 'text-yellow-500' : 'text-green-500'}>
                                {u.is_active ? 'Disable Account' : 'Enable Account'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteUser(u.user_id)} className="text-red-500">
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Projects Tab */}
            <TabsContent value="projects">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">Project</TableHead>
                      <TableHead className="text-white/60">Owner</TableHead>
                      <TableHead className="text-white/60">Type</TableHead>
                      <TableHead className="text-white/60">Status</TableHead>
                      <TableHead className="text-white/60">Created</TableHead>
                      <TableHead className="text-white/60">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((p) => (
                      <TableRow key={p.project_id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white font-medium">{p.title}</TableCell>
                        <TableCell className="text-white/60">{p.user_email || p.user_id}</TableCell>
                        <TableCell className="text-white/60 capitalize">{p.video_type}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            p.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                            p.status === 'error' ? 'bg-red-500/20 text-red-500' :
                            'bg-[#CCFF00]/20 text-[#CCFF00]'
                          }`}>
                            {p.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-white/60">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-400" onClick={() => handleDeleteProject(p.project_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">Transaction ID</TableHead>
                      <TableHead className="text-white/60">Email</TableHead>
                      <TableHead className="text-white/60">Plan</TableHead>
                      <TableHead className="text-white/60">Amount</TableHead>
                      <TableHead className="text-white/60">Status</TableHead>
                      <TableHead className="text-white/60">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.transaction_id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white font-mono text-sm">{p.transaction_id}</TableCell>
                        <TableCell className="text-white/60">{p.email}</TableCell>
                        <TableCell className="text-white capitalize">{p.plan_id}</TableCell>
                        <TableCell className="text-white">{p.currency} {p.amount}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            p.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' :
                            p.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-red-500/20 text-red-500'
                          }`}>
                            {p.payment_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-white/60">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Content Management Tab */}
            <TabsContent value="content">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Caption Styles */}
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold text-white flex items-center gap-2">
                      <Type className="w-5 h-5 text-[#CCFF00]" />
                      Caption Styles
                    </h3>
                    <Dialog open={showAddStyle} onOpenChange={setShowAddStyle}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
                          <Plus className="w-4 h-4 mr-1" />Add
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10">
                        <DialogHeader>
                          <DialogTitle className="text-white">Add Caption Style</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-white/70">ID (lowercase, no spaces)</Label>
                            <Input value={newStyle.id} onChange={(e) => setNewStyle({...newStyle, id: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. neon_glow" />
                          </div>
                          <div>
                            <Label className="text-white/70">Name</Label>
                            <Input value={newStyle.name} onChange={(e) => setNewStyle({...newStyle, name: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. Neon Glow" />
                          </div>
                          <div>
                            <Label className="text-white/70">Preview Description</Label>
                            <Input value={newStyle.preview} onChange={(e) => setNewStyle({...newStyle, preview: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. Glowing neon effect" />
                          </div>
                          <Button onClick={handleAddCaptionStyle} className="w-full bg-[#CCFF00] text-black">Add Style</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-2">
                    {captionStyles.map((style) => (
                      <div key={style.id} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                        <div>
                          <p className="text-white font-medium">{style.name}</p>
                          <p className="text-white/40 text-sm">{style.preview}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteCaptionStyle(style.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Video Types */}
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold text-white flex items-center gap-2">
                      <Video className="w-5 h-5 text-[#CCFF00]" />
                      Video Types
                    </h3>
                    <Dialog open={showAddType} onOpenChange={setShowAddType}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
                          <Plus className="w-4 h-4 mr-1" />Add
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10">
                        <DialogHeader>
                          <DialogTitle className="text-white">Add Video Type</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-white/70">ID (lowercase, no spaces)</Label>
                            <Input value={newType.id} onChange={(e) => setNewType({...newType, id: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. podcast" />
                          </div>
                          <div>
                            <Label className="text-white/70">Name</Label>
                            <Input value={newType.name} onChange={(e) => setNewType({...newType, name: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. Podcast" />
                          </div>
                          <div>
                            <Label className="text-white/70">Description</Label>
                            <Input value={newType.description} onChange={(e) => setNewType({...newType, description: e.target.value})} className="bg-[#121212] border-white/10 text-white" placeholder="e.g. Audio-focused content" />
                          </div>
                          <Button onClick={handleAddVideoType} className="w-full bg-[#CCFF00] text-black">Add Type</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-2">
                    {videoTypes.map((type) => (
                      <div key={type.id} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                        <div>
                          <p className="text-white font-medium">{type.name}</p>
                          <p className="text-white/40 text-sm">{type.description}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteVideoType(type.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing">
              <div className="grid md:grid-cols-2 gap-6">
                {pricing.map((plan) => (
                  <Card key={plan.plan_id} className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-white text-xl">{plan.name}</h3>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setEditingPrice({ price: plan.price, features: plan.features || [] });
                          setShowEditPricing(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />Edit
                      </Button>
                    </div>
                    <p className="text-4xl font-bold text-[#CCFF00] mb-4">{plan.currency} {plan.price}</p>
                    <ul className="space-y-2">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="text-white/60 text-sm">• {feature}</li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
              
              <Dialog open={showEditPricing} onOpenChange={setShowEditPricing}>
                <DialogContent className="bg-[#0A0A0A] border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-white">Edit Pricing - {selectedPlan?.name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white/70">Price (CHF)</Label>
                      <Input type="number" value={editingPrice.price} onChange={(e) => setEditingPrice({...editingPrice, price: e.target.value})} className="bg-[#121212] border-white/10 text-white" />
                    </div>
                    <div>
                      <Label className="text-white/70">Features (one per line)</Label>
                      <Textarea 
                        value={editingPrice.features?.join('\n')} 
                        onChange={(e) => setEditingPrice({...editingPrice, features: e.target.value.split('\n').filter(f => f.trim())})} 
                        className="bg-[#121212] border-white/10 text-white min-h-32" 
                      />
                    </div>
                    <Button onClick={handleUpdatePricing} className="w-full bg-[#CCFF00] text-black">Save Changes</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Announcements Tab */}
            <TabsContent value="announcements">
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-white">Announcements</h3>
                  <Dialog open={showAddAnnouncement} onOpenChange={setShowAddAnnouncement}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
                        <Plus className="w-4 h-4 mr-2" />New Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white">Create Announcement</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-white/70">Title</Label>
                          <Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})} className="bg-[#121212] border-white/10 text-white" />
                        </div>
                        <div>
                          <Label className="text-white/70">Message</Label>
                          <Textarea value={newAnnouncement.message} onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})} className="bg-[#121212] border-white/10 text-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-white/70">Type</Label>
                            <Select value={newAnnouncement.type} onValueChange={(v) => setNewAnnouncement({...newAnnouncement, type: v})}>
                              <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border-white/10">
                                <SelectItem value="info">Info</SelectItem>
                                <SelectItem value="warning">Warning</SelectItem>
                                <SelectItem value="success">Success</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-white/70">Target</Label>
                            <Select value={newAnnouncement.target} onValueChange={(v) => setNewAnnouncement({...newAnnouncement, target: v})}>
                              <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border-white/10">
                                <SelectItem value="all">All Users</SelectItem>
                                <SelectItem value="pro">Pro Users</SelectItem>
                                <SelectItem value="free">Free Users</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button onClick={handleAddAnnouncement} className="w-full bg-[#CCFF00] text-black">Create</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="space-y-4">
                  {announcements.length === 0 ? (
                    <p className="text-white/40 text-center py-8">No announcements yet</p>
                  ) : (
                    announcements.map((ann) => (
                      <div key={ann.announcement_id} className={`p-4 rounded-xl border ${
                        ann.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                        ann.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                        'bg-blue-500/10 border-blue-500/30'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-white">{ann.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs ${ann.is_active ? 'bg-green-500/20 text-green-500' : 'bg-white/10 text-white/40'}`}>
                                {ann.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">{ann.target}</span>
                            </div>
                            <p className="text-white/70 text-sm">{ann.message}</p>
                            <p className="text-white/30 text-xs mt-2">{new Date(ann.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleToggleAnnouncement(ann.announcement_id)}>
                              {ann.is_active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteAnnouncement(ann.announcement_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#CCFF00]" />
                    Daily Signups (Last 7 Days)
                  </h3>
                  <div className="space-y-2">
                    {stats?.daily_signups?.map((day, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="text-white/60 text-sm w-24">{day.date}</span>
                        <div className="flex-1 h-6 bg-[#121212] rounded overflow-hidden">
                          <div 
                            className="h-full bg-[#CCFF00]/30 rounded"
                            style={{ width: `${Math.max(day.count * 20, 5)}%` }}
                          />
                        </div>
                        <span className="text-white font-semibold w-8">{day.count}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    Daily Revenue (Last 7 Days)
                  </h3>
                  <div className="space-y-2">
                    {stats?.daily_revenue?.map((day, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="text-white/60 text-sm w-24">{day.date}</span>
                        <div className="flex-1 h-6 bg-[#121212] rounded overflow-hidden">
                          <div 
                            className="h-full bg-green-500/30 rounded"
                            style={{ width: `${Math.max(day.amount / 2, 5)}%` }}
                          />
                        </div>
                        <span className="text-white font-semibold w-16">CHF {day.amount}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl md:col-span-2">
                  <h3 className="font-heading font-semibold text-white mb-4">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-[#121212] rounded-xl">
                      <p className="text-white/60 text-sm">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-green-500">CHF {stats?.monthly_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-4 bg-[#121212] rounded-xl">
                      <p className="text-white/60 text-sm">Conversion Rate</p>
                      <p className="text-2xl font-bold text-[#CCFF00]">
                        {stats?.total_users > 0 ? ((stats?.pro_users / stats?.total_users) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                    <div className="p-4 bg-[#121212] rounded-xl">
                      <p className="text-white/60 text-sm">Completed Projects</p>
                      <p className="text-2xl font-bold text-blue-500">{stats?.completed_projects || 0}</p>
                    </div>
                    <div className="p-4 bg-[#121212] rounded-xl">
                      <p className="text-white/60 text-sm">This Week Signups</p>
                      <p className="text-2xl font-bold text-[#6E44FF]">{stats?.recent_signups || 0}</p>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default Admin;
